import {useState, useEffect, useCallback} from 'react';
import {brand, space, fontFamily} from '../brand/theme';
import {CONFIG} from '../lib/config';

interface ModelStat {
  id: string;
  name: string;
  requests: number;
  tokens: number;
}

interface UserStats {
  period: string;
  tokens: {total: number; prompt: number; completion: number};
  requests: number;
  estimated_cost_usd: number;
  models: ModelStat[];
  has_api_key: boolean;
  key_info: {name: string; spend: number} | null;
}

type Period = '1d' | '7d' | '30d' | 'all';

const PERIOD_LABELS: Record<Period, string> = {'1d': 'Сьогодні', '7d': '7 днів', '30d': 'Місяць', 'all': 'Весь час'};

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('uk-UA');
}

export function AccountTab({apiKey}: {apiKey?: string}) {
  const [period, setPeriod] = useState<Period>('7d');
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p: Period) => {
    setLoading(true);
    try {
      const headers: HeadersInit = {'Content-Type': 'application/json'};
      if (apiKey) headers['x-api-key'] = apiKey;
      // Collect all session IDs from localStorage for accurate tracking
      const sessionIds: string[] = [];
      try {
        const stored = localStorage.getItem('lapathoniia-sessions');
        if (stored) {
          const sessions = JSON.parse(stored) as Array<{id: string}>;
          sessions.forEach(s => s.id && sessionIds.push(s.id));
        }
      } catch {}
      const sessionParam = sessionIds.length > 0 ? `&session_ids=${encodeURIComponent(sessionIds.join(','))}` : '';
      const res = await fetch(`${CONFIG.backendUrl}/user/stats?period=${p}${sessionParam}`, {headers});
      if (res.ok) setStats(await res.json());
    } catch {}
    setLoading(false);
  }, [apiKey]);

  useEffect(() => { load(period); }, [period, load]);

  const card = {
    background: brand.bgCard,
    border: `1px solid ${brand.border}`,
    borderRadius: '12px',
    padding: `${space(2)} ${space(2)}`,
  };

  const sectionTitle = {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: brand.creamSubtle,
    marginBottom: space(1),
  };

  return (
    <div style={{padding: space(2), display: 'flex', flexDirection: 'column', gap: space(2), fontFamily, overflowY: 'auto', maxHeight: '100%'}}>
      {/* Header */}
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px'}}>
        <span style={{fontSize: '18px', fontWeight: 700, color: brand.cream}}>Використання</span>
        {/* Period selector */}
        <div style={{display: 'flex', gap: '4px', background: brand.bgMuted, borderRadius: '8px', padding: '3px'}}>
          {(Object.keys(PERIOD_LABELS) as Period[]).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: 600, fontFamily,
                background: period === p ? brand.bgElevated : 'transparent',
                color: period === p ? brand.cream : brand.creamSubtle,
                transition: 'all 150ms ease',
              }}
            >{PERIOD_LABELS[p]}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{textAlign: 'center', color: brand.creamSubtle, padding: space(3)}}>Завантаження…</div>
      ) : !stats || stats.requests === 0 ? (
        <div style={{...card, textAlign: 'center', color: brand.creamSubtle, padding: space(3)}}>
          <div style={{fontSize: '32px', marginBottom: '8px'}}>📊</div>
          <div>За цей період немає даних</div>
          <div style={{fontSize: '12px', marginTop: '4px'}}>Починайте чат — і статистика з'явиться</div>
        </div>
      ) : (
        <>
          {/* Key stats */}
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
            <div style={card}>
              <div style={sectionTitle}>Токени</div>
              <div style={{fontSize: '28px', fontWeight: 700, color: brand.tealLight, lineHeight: 1}}>{fmt(stats.tokens.total)}</div>
              <div style={{fontSize: '12px', color: brand.creamSubtle, marginTop: '4px'}}>
                {fmt(stats.tokens.prompt)} вхід · {fmt(stats.tokens.completion)} вихід
              </div>
            </div>
            <div style={card}>
              <div style={sectionTitle}>Запити</div>
              <div style={{fontSize: '28px', fontWeight: 700, color: brand.gold, lineHeight: 1}}>{fmt(stats.requests)}</div>
              {stats.estimated_cost_usd > 0 && (
                <div style={{fontSize: '12px', color: brand.creamSubtle, marginTop: '4px'}}>
                  ~${stats.estimated_cost_usd.toFixed(4)}
                </div>
              )}
            </div>
          </div>

          {/* Model breakdown */}
          {stats.models.length > 0 && (
            <div style={card}>
              <div style={sectionTitle}>Моделі</div>
              <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                {stats.models.map(m => {
                  const pct = stats.tokens.total > 0 ? m.tokens / stats.tokens.total : 0;
                  return (
                    <div key={m.id}>
                      <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                        <span style={{fontSize: '13px', color: brand.cream, fontWeight: 500}}>{m.name}</span>
                        <span style={{fontSize: '12px', color: brand.creamSubtle}}>{fmt(m.tokens)} · {m.requests} зап.</span>
                      </div>
                      <div style={{height: '4px', background: brand.bgMuted, borderRadius: '2px', overflow: 'hidden'}}>
                        <div style={{height: '100%', width: `${pct * 100}%`, background: brand.tealLight, borderRadius: '2px', transition: 'width 400ms ease'}} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* API key info */}
          {stats.key_info && (
            <div style={card}>
              <div style={sectionTitle}>API Key</div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '13px', color: brand.cream, fontFamily: 'monospace'}}>{stats.key_info.name}</span>
                <span style={{fontSize: '12px', color: brand.creamSubtle}}>${stats.key_info.spend.toFixed(4)} витрачено</span>
              </div>
            </div>
          )}

          {/* Shared user note */}
          {!stats.has_api_key && (
            <div style={{background: 'rgba(240,208,64,0.06)', border: `1px solid rgba(240,208,64,0.15)`, borderRadius: '10px', padding: `${space(1.5)} ${space(2)}`, fontSize: '12px', color: brand.creamMuted}}>
              <div style={{fontWeight: 600, color: brand.gold, marginBottom: '4px'}}>📢 Спільний ключ</div>
              Ця статистика охоплює всіх користувачів спільного демо-ключа, а не тільки вас.<br/>
              <span style={{marginTop: '4px', display: 'block'}}>Щоб бачити власну статистику — <a href="https://lapathoniia.top/usage" target="_blank" rel="noreferrer" style={{color: brand.tealLight}}>додайте персональний API ключ</a>.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
